import {
  BANDS_DJS_TAXONOMY_KEY,
  BAR_SERVICES_TAXONOMY_KEY,
  CAKES_DESSERTS_TAXONOMY_KEY,
  CATERING_TAXONOMY_KEY,
  FLORISTS_TAXONOMY_KEY,
  HAIR_MAKEUP_TAXONOMY_KEY,
  PHOTOGRAPHERS_TAXONOMY_KEY,
  PLANNERS_TAXONOMY_KEY,
  VENUES_TAXONOMY_KEY,
  VIDEOGRAPHERS_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorWordSeoCategory } from '@zola-helpers/client/dist/es/marketplace/vendorWords';
import { SeoSrpMetricDataView } from '@zola/svc-marketplace-ts-types';

import { formatAsCurrency, lowercaseFirst, stripSpaces } from '~/util/textUtils';

import { pickCanonicalFacets } from '../../../util/canonicalUrl';
import {
  SEARCH_RESULTS_SEO_CATEGORY_TEMPLATE,
  SEARCH_RESULTS_SEO_DESCRIPTION_TEMPLATE,
} from '../meta/searchResultsSeoMeta';
import { VendorSearchRequest } from '../types/types';
import { getCanonicalFacetDescription } from '../utils/canonicalFacetHelpers';
import { getInLocation, getLocation, getMetaImageUrl } from '../utils/seoProviderHelpers';
import {
  SearchResultPageSearchResult,
  useSearchResultsPageContext,
} from './SearchResultsPageContext';
import { SearchResultsSeoContext } from './SearchResultsSeoContext';

const getPreAuthResultsCount = (
  vendorSearch: VendorSearchRequest,
  searchResults: SearchResultPageSearchResult,
  seoMetrics: SeoSrpMetricDataView | null
) => {
  const { selectedTaxonomy } = vendorSearch;
  const { totalHits } = searchResults;
  const { storefrontCount } = seoMetrics || {};
  let resultsCount = storefrontCount || totalHits;
  if (selectedTaxonomy?.key === VENUES_TAXONOMY_KEY) {
    resultsCount = totalHits;
  }
  resultsCount = Math.min(resultsCount, 10);
  return resultsCount < 4 ? '' : resultsCount.toString();
};

const getPreAuthPrefix = (
  vendorSearch: VendorSearchRequest,
  searchResults: SearchResultPageSearchResult,
  seoMetrics: SeoSrpMetricDataView | null,
  includeCount: boolean
) => {
  const { selectedTaxonomy, selectedFacets } = vendorSearch;
  const taxonomyKey = selectedTaxonomy?.key;
  const facetKey = pickCanonicalFacets(taxonomyKey, selectedFacets || [], false)[0]?.key;
  const resultsCount = includeCount
    ? getPreAuthResultsCount(vendorSearch, searchResults, seoMetrics)
    : '';
  // Some specific category searches (e.g. "Affordable") don't start with "The Best"
  if (
    taxonomyKey === BANDS_DJS_TAXONOMY_KEY ||
    taxonomyKey === CATERING_TAXONOMY_KEY ||
    taxonomyKey === FLORISTS_TAXONOMY_KEY ||
    taxonomyKey === HAIR_MAKEUP_TAXONOMY_KEY ||
    taxonomyKey === PHOTOGRAPHERS_TAXONOMY_KEY ||
    taxonomyKey === PLANNERS_TAXONOMY_KEY ||
    taxonomyKey === VENUES_TAXONOMY_KEY ||
    taxonomyKey === VIDEOGRAPHERS_TAXONOMY_KEY
  ) {
    if (
      vendorSearch.price?.max ||
      facetKey === 'videographer-package-components-livestream-services'
    ) {
      return resultsCount;
    }
  }
  if (taxonomyKey === CATERING_TAXONOMY_KEY) {
    if (
      facetKey === 'caterer-dietary-restrictions-gluten-free' ||
      facetKey === 'caterer-dietary-restrictions-organic'
    ) {
      return '';
    }
    if (
      facetKey === 'caterer-dietary-restrictions-vegan' ||
      facetKey === 'caterer-dietary-restrictions-vegetarian'
    ) {
      return 'Top';
    }
  }
  return `The Best ${resultsCount}`;
};

const getPreAuthCategory = (vendorSearch: VendorSearchRequest) => {
  const { selectedTaxonomy, selectedFacets } = vendorSearch;
  const taxonomyKey = selectedTaxonomy?.key;
  const facetKey = pickCanonicalFacets(taxonomyKey, selectedFacets || [], false)[0]?.key;
  const facetDescription = getCanonicalFacetDescription(taxonomyKey, selectedFacets, true) || '';
  let category = SEARCH_RESULTS_SEO_CATEGORY_TEMPLATE[facetKey];
  if (!category) {
    let key = '';
    if (taxonomyKey === BANDS_DJS_TAXONOMY_KEY) {
      if (vendorSearch.price?.max) {
        key = 'bands-djs-price-max';
      }
    } else if (taxonomyKey === CATERING_TAXONOMY_KEY) {
      if (vendorSearch.price?.max) {
        key = 'catering-price-max';
      }
    } else if (taxonomyKey === FLORISTS_TAXONOMY_KEY) {
      if (vendorSearch.price?.max) {
        key = 'florists-price-max';
      }
    } else if (taxonomyKey === HAIR_MAKEUP_TAXONOMY_KEY) {
      if (vendorSearch.price?.max) {
        key = 'hair-makeup-price-max';
      }
    } else if (taxonomyKey === PHOTOGRAPHERS_TAXONOMY_KEY) {
      if (vendorSearch.price?.max) {
        key = 'photographers-price-max';
      }
    } else if (taxonomyKey === PLANNERS_TAXONOMY_KEY) {
      if (vendorSearch.price?.max) {
        key = 'planners-price-max';
      }
    } else if (taxonomyKey === VENUES_TAXONOMY_KEY) {
      if (vendorSearch.price?.max) {
        key = 'venues-price-max';
      } else if (vendorSearch.capacity?.max === 50) {
        key = 'venues-capacity-max-50';
      }
    } else if (taxonomyKey === VIDEOGRAPHERS_TAXONOMY_KEY) {
      if (vendorSearch.price?.max) {
        key = 'videographers-price-max';
      }
    }
    category =
      SEARCH_RESULTS_SEO_CATEGORY_TEMPLATE[key] ||
      stripSpaces(`${facetDescription} Wedding ${getVendorWordSeoCategory(taxonomyKey)}`);
  }
  return category;
};

const getPreAuthHeadingSegments = (
  vendorSearch: VendorSearchRequest,
  searchResults: SearchResultPageSearchResult,
  seoMetrics: SeoSrpMetricDataView | null
) => {
  const headingPrefix = getPreAuthPrefix(vendorSearch, searchResults, seoMetrics, false);
  const headingCategory = getPreAuthCategory(vendorSearch);
  const headingLocation = getLocation(vendorSearch);
  const heading = stripSpaces(
    `${headingPrefix} ${headingCategory} ${getInLocation(headingLocation)}`
  );
  return { heading, headingPrefix, headingCategory, headingLocation };
};

const getPreAuthTitle = (
  vendorSearch: VendorSearchRequest,
  searchResults: SearchResultPageSearchResult,
  seoMetrics: SeoSrpMetricDataView | null
) => {
  const prefix = getPreAuthPrefix(vendorSearch, searchResults, seoMetrics, true);
  const category = getPreAuthCategory(vendorSearch);
  const location = getLocation(vendorSearch);
  return stripSpaces(`${prefix} ${category} ${getInLocation(location)} - Zola`);
};

const getPriceRange = (minPriceCents: number | null, maxPriceCents: number | null) => {
  const minPrice = minPriceCents ? formatAsCurrency(minPriceCents / 100) : null;
  const maxPrice = maxPriceCents ? formatAsCurrency(maxPriceCents / 100) : null;
  let priceRange = '';
  if (minPrice) {
    priceRange = minPrice;
  }
  if (maxPrice && maxPrice !== minPrice) {
    priceRange += `-${maxPrice}`;
  }
  if (!priceRange) {
    return '';
  }
  return priceRange;
};

const getReviewCount = (vendorSearch: VendorSearchRequest, reviewCount: number) => {
  const { selectedTaxonomy } = vendorSearch;
  const taxonomyKey = selectedTaxonomy?.key;
  if (reviewCount < 10) {
    return '';
  }
  let count = reviewCount.toString();
  if (reviewCount > 25) {
    if (taxonomyKey === CAKES_DESSERTS_TAXONOMY_KEY || taxonomyKey === PLANNERS_TAXONOMY_KEY) {
      count = 'Over 25';
    } else {
      count = '25+';
    }
  }
  return count;
};

const getMetaDescription = (
  vendorSearch: VendorSearchRequest,
  searchResults: SearchResultPageSearchResult
) => {
  const { selectedTaxonomy, selectedLocation, selectedFacets } = vendorSearch;
  const taxonomyKey = selectedTaxonomy?.key;
  const facetKey = pickCanonicalFacets(taxonomyKey, selectedFacets || [], false)[0]?.key;
  const facetDescription = lowercaseFirst(
    getCanonicalFacetDescription(taxonomyKey, selectedFacets, false) || ''
  );
  const location = getLocation(vendorSearch);
  let minPriceCents: number | null = null;
  let maxPriceCents: number | null = null;
  let reviewCount = 0;
  let description = '';
  searchResults.vendors.forEach((vendor) => {
    if (vendor.priceRangeVisible) {
      if (vendor.price?.min && (!minPriceCents || vendor.price.min < minPriceCents)) {
        minPriceCents = vendor.price.min;
      }
      if (vendor.price?.max && (!maxPriceCents || vendor.price.max > maxPriceCents)) {
        maxPriceCents = vendor.price.max;
      }
    }
    reviewCount += vendor.reviewsCount;
  });
  description = SEARCH_RESULTS_SEO_DESCRIPTION_TEMPLATE[facetKey];
  if (!description) {
    let key = '';
    if (taxonomyKey === BANDS_DJS_TAXONOMY_KEY) {
      if (vendorSearch.price?.max) {
        key = 'bands-djs-price-max';
      } else if (selectedLocation?.type === 'STATE') {
        key = 'bands-djs-location-type-state';
      } else {
        key = 'bands-djs-default';
      }
    } else if (taxonomyKey === BAR_SERVICES_TAXONOMY_KEY) {
      key = 'bar-services-default';
    } else if (taxonomyKey === CAKES_DESSERTS_TAXONOMY_KEY) {
      key = 'cakes-desserts-default';
    } else if (taxonomyKey === CATERING_TAXONOMY_KEY) {
      if (vendorSearch.price?.max) {
        key = 'catering-price-max';
      } else if (selectedLocation?.type === 'STATE') {
        key = 'catering-location-type-state';
      } else {
        key = 'catering-default';
      }
    } else if (taxonomyKey === FLORISTS_TAXONOMY_KEY) {
      if (vendorSearch.price?.max) {
        key = 'florists-price-max';
      } else if (selectedLocation?.type === 'STATE') {
        key = 'florists-location-type-state';
      } else {
        key = 'florists-default';
      }
    } else if (taxonomyKey === HAIR_MAKEUP_TAXONOMY_KEY) {
      if (vendorSearch.price?.max) {
        key = 'hair-makeup-price-max';
      } else {
        key = 'hair-makeup-default';
      }
    } else if (taxonomyKey === PHOTOGRAPHERS_TAXONOMY_KEY) {
      if (vendorSearch.price?.max) {
        key = 'photographers-price-max';
      } else if (selectedLocation?.type === 'STATE') {
        key = 'photographers-location-type-state';
      } else {
        key = 'photographers-default';
      }
    } else if (taxonomyKey === PLANNERS_TAXONOMY_KEY) {
      if (vendorSearch.price?.max) {
        key = 'planners-price-max';
      } else {
        key = 'planners-default';
      }
    } else if (taxonomyKey === VENUES_TAXONOMY_KEY) {
      if (vendorSearch.price?.max) {
        key = 'venues-price-max';
      } else if (vendorSearch.capacity?.max === 50) {
        key = 'venues-capacity-max-50';
      } else {
        key = 'venues-default';
      }
    } else if (taxonomyKey === VIDEOGRAPHERS_TAXONOMY_KEY) {
      if (vendorSearch.price?.max) {
        key = 'videographers-price-max';
      } else if (selectedLocation?.type === 'STATE') {
        key = 'videographers-location-type-state';
      } else {
        key = 'videographers-default';
      }
    }
    description =
      SEARCH_RESULTS_SEO_DESCRIPTION_TEMPLATE[key] ||
      'Find the best wedding vendors. See prices, photos and availability for your special day.';
  }
  description = description.replace('{{FACET_DESCRIPTION}}', facetDescription);
  description = description.replace('{{LOCATION}}', getInLocation(location));
  if (description.includes('{{#IF_PRICE}}') || description.includes('{{PRICE_RANGE}}')) {
    const priceRange = getPriceRange(minPriceCents, maxPriceCents);
    if (priceRange) {
      description = description.replace('{{PRICE_RANGE}}', priceRange);
      description = description.replace(/\{\{.IF_PRICE\}\}/g, '');
    } else {
      description = description.replace(/\{\{#IF_PRICE\}\}.*\{\{\/IF_PRICE\}\}/g, '');
    }
  }
  if (description.includes('{{#IF_REVIEW}}') || description.includes('{{REVIEW_COUNT}}')) {
    const count = getReviewCount(vendorSearch, reviewCount);
    if (count) {
      description = description.replace('{{REVIEW_COUNT}}', count);
      description = description.replace(/\{\{.IF_REVIEW\}\}/g, '');
    } else {
      description = description.replace(/\{\{#IF_REVIEW\}\}.*\{\{\/IF_REVIEW\}\}/g, '');
    }
  }
  return stripSpaces(description);
};

const isNoIndex = (
  vendorSearch: VendorSearchRequest,
  canonicalUrl: string,
  seoCanonicalUrl: string,
  searchResults: SearchResultPageSearchResult,
  seoMetrics: SeoSrpMetricDataView | null
) => {
  const { page } = vendorSearch || {};

  // We don't want to index pages beyond the first page but it will still allow google to follow links on the page
  if (page && page > 1) return true;

  // No vendors = no index
  if (searchResults?.totalHits === 0) return true;

  // If an SRP does not self canonicalize, add a noindex.
  if (seoCanonicalUrl !== canonicalUrl) return true;

  // Venues are based on radius, not exact location match
  // Since there are results, it means there are venues in radius so index it
  if (vendorSearch?.selectedTaxonomy?.key === VENUES_TAXONOMY_KEY) {
    return false;
  }

  // If seoMetrics are available, we can make a decision based on the data
  // If not available, err on the side of caution and index it
  if (seoMetrics !== null && seoMetrics.noIndex) {
    return true;
  }

  // Let's index this SRP
  return false;
};

export const PreAuthSearchResultsSeoProvider: React.FC = ({ children }) => {
  const { vendorSearch, canonicalUrl, seoCanonicalUrl, searchResults, seoMetrics } =
    useSearchResultsPageContext();
  const { heading, headingPrefix, headingCategory, headingLocation } = getPreAuthHeadingSegments(
    vendorSearch,
    searchResults,
    seoMetrics
  );
  const title = getPreAuthTitle(vendorSearch, searchResults, seoMetrics);
  const description = getMetaDescription(vendorSearch, searchResults);
  const imageUrl = getMetaImageUrl(searchResults);
  const noIndex = isNoIndex(vendorSearch, canonicalUrl, seoCanonicalUrl, searchResults, seoMetrics);
  return (
    <SearchResultsSeoContext.Provider
      value={{
        title,
        heading,
        headingPrefix,
        headingCategory,
        headingLocation,
        description,
        canonicalUrl: seoCanonicalUrl,
        imageUrl,
        noIndex,
      }}
    >
      {children}
    </SearchResultsSeoContext.Provider>
  );
};
